/**
 * Status Data Service
 *
 * Fetches admin diagnostic data via Multicall3-batched view calls.
 * Data is fetched in 3 sequential batches due to cascading address resolution:
 *   Batch 1: RaffleMining addresses + simple state
 *   Batch 2: GameHistory + Crank (requires addresses from Batch 1)
 *   Batch 3: Round details + finalization + pending work (requires roundId from Batch 2)
 */

import { Interface } from 'ethers'
import { getChainProvider } from '@/lib/chainProvider'
import { CONTRACT_ADDRESS, VIEWS_CONTRACT_ADDRESS, SWAPPER_ADDRESS, WELEPHANT_ADDRESS, STAKING_VAULT_ADDRESS, PAYDAY_INFO_LIST_ADDRESS, GAME_CHAIN_ID, ROUND_PHASE_NAMES, getMulticall3AddressForChain } from '@/lib/contracts'
import { createMulticallService } from '@/services/multicallService'
import { RaffleMiningABI } from '@/abi/RaffleMiningABI'
import { GameHistoryABI } from '@/abi/GameHistoryABI'
import { RaffleMiningViewsABI } from '@/abi/RaffleMiningViewsABI'
import { RaffleMiningCrankABI } from '@/abi/RaffleMiningCrankABI'
import { CrankFinalizationABI } from '@/abi/CrankFinalizationABI'
import { Multicall3ABI } from '@/abi/Multicall3ABI'

// --- Types ---

export interface ContractAddressInfo {
  name: string
  address: string
}

export interface WiringCheck {
  label: string
  actual: string
  expected: string
  ok: boolean
}

export interface GameState {
  paused: boolean
  currentRound: bigint
  completedRounds: bigint
  contractBalance: bigint
  paydayFund: bigint
  gasChargePool: bigint
  maxGasChargePool: bigint
  activeAutoPlays: bigint
  pendingPlays: bigint
  minPlayersPerRound: bigint
}

export interface CurrentRoundDetails {
  roundId: bigint
  phase: number
  phaseName: string
  timeLeft: bigint
  finalized: boolean
  hasMinPlayers: boolean
  participantCount: bigint
  startTime: bigint
  endTime: bigint
  closingBlock: bigint
  totalPot: bigint
  winningSquare: bigint
  isPayday: boolean
  isSingleWinner: boolean
  singleWinner: string
}

export interface FinalizationState {
  singleWinnerSelection: boolean
  welephantDistribution: boolean
  ethDistribution: boolean
  distributionRoundId: bigint | null
}

export interface PendingWork {
  workAvailable: boolean
  roundReadyToFinalize: boolean
  roundToFinalize: bigint
  currentRoundFinalized: boolean
  autoPlaysRemaining: bigint
  pendingPlaysRemaining: bigint
  welephantDistributionsRemaining: bigint
  ethDistributionsRemaining: bigint
  singleWinnerSelectionInProgress: boolean
  singleWinnerRound: bigint
  roundBeingDistributed: bigint
  vaultReservesAvailable: bigint
}

export type SystemStatus = 'operational' | 'halted' | 'paused' | 'loading'

export interface StatusData {
  addresses: ContractAddressInfo[]
  wiringChecks: WiringCheck[]
  gameState: GameState
  currentRound: CurrentRoundDetails
  finalizationState: FinalizationState
  pendingWork: PendingWork | null
  systemStatus: SystemStatus
  haltReason: string | null
  blockNumber: bigint
  blockTimestamp: bigint
}

// --- Helpers ---

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

function isConfigured(addr: string | null | undefined): addr is string {
  return !!addr && addr !== ZERO_ADDRESS
}

function addrMatch(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

function deriveSystemStatus(
  paused: boolean,
  round: { finalized: boolean; hasMinPlayers: boolean; closingBlock: bigint; endTime: bigint },
  blockTimestamp: bigint,
  roundId: bigint,
): { status: SystemStatus; reason: string | null } {
  if (paused) return { status: 'paused', reason: 'Game is paused' }
  if (!round.finalized && round.hasMinPlayers && round.closingBlock === 0n) {
    if (blockTimestamp > round.endTime && round.endTime > 0n) {
      return { status: 'halted', reason: `Round ${roundId} expired but closingBlock == 0` }
    }
  }
  return { status: 'operational', reason: null }
}

// --- Fetch ---

export async function fetchStatusData(): Promise<StatusData> {
  const provider = await getChainProvider(GAME_CHAIN_ID)
  const multicallAddr = getMulticall3AddressForChain(GAME_CHAIN_ID)
  const multicall = createMulticallService(provider, multicallAddr, GAME_CHAIN_ID)

  const rmIface = new Interface(RaffleMiningABI)
  const ghIface = new Interface(GameHistoryABI)
  const viewsIface = new Interface(RaffleMiningViewsABI)
  const crankIface = new Interface(RaffleMiningCrankABI)
  const finIface = new Interface(CrankFinalizationABI)
  const mc3Iface = new Interface(Multicall3ABI)

  // ---- Batch 1: Core addresses + simple state from RaffleMining ----
  const batch1 = [
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'crankContract'),           // 0
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'gameHistory'),             // 1
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'welephantVault', [], true),// 2
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'feeCollector'),            // 3
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'stakingVault'),            // 4
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'autoPlayRegistry', [], true), // 5
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'verification'),            // 6
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'paused'),                  // 7
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'paydayFund'),              // 8
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'gasChargePool'),           // 9
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'maxGasChargePool'),        // 10
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'activeAutoPlayCount'),     // 11
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'getPendingPlaysLength'),   // 12
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'minPlayersPerRound'),     // 13
    multicall.encodeCall(multicallAddr, mc3Iface, 'getEthBalance', [CONTRACT_ADDRESS]), // 14
    multicall.encodeCall(multicallAddr, mc3Iface, 'getBlockNumber'),            // 15
    multicall.encodeCall(multicallAddr, mc3Iface, 'getCurrentBlockTimestamp'),   // 16
  ]

  const b1 = await multicall.callAndDecode<[
    string, string, string | null, string, string, string | null, string,
    boolean, bigint, bigint, bigint, bigint, bigint, bigint,
    bigint, bigint, bigint,
  ]>(batch1)

  const crankAddr = b1[0]!
  const gameHistoryAddr = b1[1]!
  const welephantVaultAddr = b1[2]
  const feeCollectorAddr = b1[3]!
  const stakingVaultAddr = b1[4]!
  const autoPlayRegistryAddr = b1[5]
  const verificationAddr = b1[6]!
  const paused = b1[7]!
  const paydayFund = b1[8]!
  const gasChargePool = b1[9]!
  const maxGasChargePool = b1[10]!
  const activeAutoPlays = b1[11]!
  const pendingPlays = b1[12]!
  const minPlayersPerRound = b1[13]!
  const contractBalance = b1[14]!
  const blockNumber = b1[15]!
  const blockTimestamp = b1[16]!

  // ---- Batch 2: GameHistory round info + Crank finalization address ----
  const batch2Calls = [
    multicall.encodeCall(gameHistoryAddr, ghIface, 'currentRoundId'),           // 0
    multicall.encodeCall(gameHistoryAddr, ghIface, 'getTotalRoundSummaries'),   // 1
  ]

  if (isConfigured(crankAddr)) {
    batch2Calls.push(
      multicall.encodeCall(crankAddr, crankIface, 'finalizationContract', [], true), // 2
    )
  }

  const b2 = await multicall.callAndDecode<[bigint, bigint, string | null]>(batch2Calls)

  const currentRoundId = b2[0]!
  const completedRounds = b2[1]!
  const finalizationAddr = b2.length > 2 ? b2[2] : null

  // ---- Batch 3: Round details + finalization state + pending work ----
  const batch3Calls = [
    multicall.encodeCall(gameHistoryAddr, ghIface, 'getRoundCore', [currentRoundId]),  // 0
    multicall.encodeCall(CONTRACT_ADDRESS, rmIface, 'getRoundPhase', [currentRoundId]),// 1
    multicall.encodeCall(VIEWS_CONTRACT_ADDRESS, viewsIface, 'getPendingWork', [], true), // 2
  ]

  // Add finalization calls if address is available
  const hasFinalization = isConfigured(finalizationAddr)
  if (hasFinalization) {
    batch3Calls.push(
      multicall.encodeCall(finalizationAddr!, finIface, 'crankCore', [], true),                        // 3
      multicall.encodeCall(finalizationAddr!, finIface, 'welephantVault', [], true),                   // 4
      multicall.encodeCall(finalizationAddr!, finIface, 'feeAccounting', [], true),                    // 5
      multicall.encodeCall(finalizationAddr!, finIface, 'isSingleWinnerSelectionInProgress', [], true),// 6
      multicall.encodeCall(finalizationAddr!, finIface, 'isWelephantDistributionInProgress', [], true),// 7
      multicall.encodeCall(finalizationAddr!, finIface, 'isEthDistributionInProgress', [], true),      // 8
      multicall.encodeCall(finalizationAddr!, finIface, 'getDistributionRoundId', [], true),           // 9
    )
  }

  const b3 = await multicall.callAndDecode<unknown[]>(batch3Calls)

  // Decode round core (tuple)
  const roundCore = b3[0] as {
    startTime: bigint
    endTime: bigint
    startBlock: bigint
    endBlock: bigint
    closingBlock: bigint
    totalPot: bigint
    gameFee: bigint
    finalized: boolean
    winningSquare: bigint
    isPayday: boolean
    isSingleWinner: boolean
    singleWinner: string
    hasMinPlayers: boolean
    participantCount: bigint
  }

  // Decode phase (returns [phase, timeLeft])
  const phaseResult = b3[1] as [bigint, bigint]
  const phase = Number(phaseResult[0])
  const timeLeft = phaseResult[1]

  // Decode pending work
  const rawPendingWork = b3[2] as {
    autoPlaysRemaining: bigint
    pendingPlaysRemaining: bigint
    roundReadyToFinalize: boolean
    roundToFinalize: bigint
    welephantDistributionsRemaining: bigint
    ethDistributionsRemaining: bigint
    roundBeingDistributed: bigint
    singleWinnerSelectionInProgress: boolean
    singleWinnerRound: bigint
    currentRoundFinalized: boolean
    vaultReservesAvailable: bigint
    workAvailable: boolean
  } | null

  // Decode finalization state
  let finCrankCore: string | null = null
  let finWelephantVault: string | null = null
  let finFeeAccounting: string | null = null
  let finSingleWinner = false
  let finWelDist = false
  let finEthDist = false
  let finDistRoundId: bigint | null = null

  if (hasFinalization) {
    finCrankCore = b3[3] as string | null
    finWelephantVault = b3[4] as string | null
    finFeeAccounting = b3[5] as string | null
    finSingleWinner = (b3[6] as boolean | null) ?? false
    finWelDist = (b3[7] as boolean | null) ?? false
    finEthDist = (b3[8] as boolean | null) ?? false
    finDistRoundId = (b3[9] as bigint | null) ?? null
  }

  // ---- Build addresses list ----
  const addresses: ContractAddressInfo[] = []
  if (WELEPHANT_ADDRESS) {
    addresses.push({ name: 'WELEPHANT', address: WELEPHANT_ADDRESS })
  }
  if (STAKING_VAULT_ADDRESS) {
    addresses.push({ name: 'stWELEPHANT', address: STAKING_VAULT_ADDRESS })
  }
  addresses.push(
    { name: 'RaffleMining', address: CONTRACT_ADDRESS },
    { name: 'GameHistory', address: gameHistoryAddr },
    { name: 'RaffleMiningCrank', address: crankAddr },
  )
  if (isConfigured(finalizationAddr)) {
    addresses.push({ name: 'CrankFinalization', address: finalizationAddr! })
  }
  if (isConfigured(welephantVaultAddr)) {
    addresses.push({ name: 'WelephantVault', address: welephantVaultAddr! })
  }
  addresses.push(
    { name: 'FeeCollector', address: feeCollectorAddr },
    { name: 'StakingVault', address: stakingVaultAddr },
  )
  if (finFeeAccounting != null) {
    addresses.push({ name: 'FeeAccounting', address: finFeeAccounting })
  }
  if (isConfigured(autoPlayRegistryAddr)) {
    addresses.push({ name: 'AutoPlayRegistry', address: autoPlayRegistryAddr! })
  }
  addresses.push(
    { name: 'Verification', address: verificationAddr },
  )
  if (VIEWS_CONTRACT_ADDRESS) {
    addresses.push({ name: 'RaffleMiningViews', address: VIEWS_CONTRACT_ADDRESS })
  }
  if (SWAPPER_ADDRESS) {
    addresses.push({ name: 'WelephantSwap', address: SWAPPER_ADDRESS })
  }
  if (PAYDAY_INFO_LIST_ADDRESS) {
    addresses.push({ name: 'PayDayInfoList', address: PAYDAY_INFO_LIST_ADDRESS })
  }

  // ---- Build wiring checks ----
  const wiringChecks: WiringCheck[] = []
  if (hasFinalization) {
    if (finCrankCore != null) {
      wiringChecks.push({
        label: 'CrankFinalization \u2192 CrankCore',
        actual: finCrankCore,
        expected: crankAddr,
        ok: addrMatch(finCrankCore, crankAddr),
      })
    }
    if (finWelephantVault != null && isConfigured(welephantVaultAddr)) {
      wiringChecks.push({
        label: 'CrankFinalization \u2192 WelephantVault',
        actual: finWelephantVault,
        expected: welephantVaultAddr!,
        ok: addrMatch(finWelephantVault, welephantVaultAddr!),
      })
    }
    if (finFeeAccounting != null) {
      wiringChecks.push({
        label: 'CrankFinalization \u2192 FeeAccounting',
        actual: finFeeAccounting,
        expected: finFeeAccounting,
        ok: true,
      })
    }
  }
  // Also check Crank -> CrankFinalization
  if (isConfigured(finalizationAddr)) {
    wiringChecks.unshift({
      label: 'Crank \u2192 CrankFinalization',
      actual: finalizationAddr!,
      expected: finalizationAddr!,  // resolved onchain, always matches itself
      ok: true,
    })
  }

  // ---- Build current round ----
  const phaseName = ROUND_PHASE_NAMES[phase] ?? `unknown(${phase})`
  const currentRound: CurrentRoundDetails = {
    roundId: currentRoundId,
    phase,
    phaseName,
    timeLeft,
    finalized: roundCore.finalized,
    hasMinPlayers: roundCore.hasMinPlayers,
    participantCount: roundCore.participantCount,
    startTime: roundCore.startTime,
    endTime: roundCore.endTime,
    closingBlock: roundCore.closingBlock,
    totalPot: roundCore.totalPot,
    winningSquare: roundCore.winningSquare,
    isPayday: roundCore.isPayday,
    isSingleWinner: roundCore.isSingleWinner,
    singleWinner: roundCore.singleWinner,
  }

  // ---- Build pending work ----
  const pendingWork: PendingWork | null = rawPendingWork ? {
    workAvailable: rawPendingWork.workAvailable,
    roundReadyToFinalize: rawPendingWork.roundReadyToFinalize,
    roundToFinalize: rawPendingWork.roundToFinalize,
    currentRoundFinalized: rawPendingWork.currentRoundFinalized,
    autoPlaysRemaining: rawPendingWork.autoPlaysRemaining,
    pendingPlaysRemaining: rawPendingWork.pendingPlaysRemaining,
    welephantDistributionsRemaining: rawPendingWork.welephantDistributionsRemaining,
    ethDistributionsRemaining: rawPendingWork.ethDistributionsRemaining,
    singleWinnerSelectionInProgress: rawPendingWork.singleWinnerSelectionInProgress,
    singleWinnerRound: rawPendingWork.singleWinnerRound,
    roundBeingDistributed: rawPendingWork.roundBeingDistributed,
    vaultReservesAvailable: rawPendingWork.vaultReservesAvailable,
  } : null

  // ---- Derive system status ----
  const { status: systemStatus, reason: haltReason } = deriveSystemStatus(
    paused, roundCore, blockTimestamp, currentRoundId,
  )

  return {
    addresses,
    wiringChecks,
    gameState: {
      paused,
      currentRound: currentRoundId,
      completedRounds,
      contractBalance,
      paydayFund,
      gasChargePool,
      maxGasChargePool,
      activeAutoPlays,
      pendingPlays,
      minPlayersPerRound,
    },
    currentRound,
    finalizationState: {
      singleWinnerSelection: finSingleWinner,
      welephantDistribution: finWelDist,
      ethDistribution: finEthDist,
      distributionRoundId: (finWelDist || finEthDist) ? finDistRoundId : null,
    },
    pendingWork,
    systemStatus,
    haltReason,
    blockNumber,
    blockTimestamp,
  }
}
